import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check, Download, ImageOff, Heart, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PROVIDER_META, findModel } from "@/lib/meta";

const CodeBlock = ({ children, className, ...props }) => {
  const [copied, setCopied] = useState(false);
  const isBlock = className?.startsWith("language-") || String(children).includes("\n");
  if (!isBlock) return <code className={className} {...props}>{children}</code>;
  const copy = () => {
    navigator.clipboard.writeText(String(children));
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span className="relative block group">
      <button onClick={copy} className="absolute right-2 top-2 p-1.5 rounded-md bg-[#1F2338] text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Copy code">
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      <code className={className} {...props}>{children}</code>
    </span>
  );
};

export const Markdown = ({ children }) => (
  <div className="md text-[15px]"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{ code: CodeBlock }}>{children}</ReactMarkdown></div>
);

const GeneratedImage = ({ src, message, index }) => {
  const [state, setState] = useState("idle");
  const pin = async () => {
    setState("saving");
    try {
      await api.post("/favorites", { image: src, prompt: message.prompt || "", provider: message.provider, model: message.model });
      setState("saved");
      toast.success("Pinned to your gallery");
    } catch (err) {
      setState("idle");
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="relative group rounded-xl overflow-hidden border border-slate-800">
      <img src={src} alt="Generated" className="block max-w-full max-h-[560px] object-contain bg-black" data-testid={`generated-image-${message.id}-${index}`} />
      <div className="absolute right-3 top-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={pin} disabled={state !== "idle"} data-testid={`pin-image-${message.id}-${index}`} className={`p-2 rounded-lg backdrop-blur transition-colors ${state === "saved" ? "bg-rose-500/80 text-white" : "bg-[#0D0F18]/80 text-slate-200 hover:text-rose-300"}`} aria-label="Pin to gallery" title="Pin to gallery">
          {state === "saving" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className={`w-4 h-4 ${state === "saved" ? "fill-current" : ""}`} />}
        </button>
        <a href={src} download={`omnimodel-${message.id}-${index}.png`} data-testid={`download-image-${message.id}-${index}`} className="p-2 rounded-lg bg-[#0D0F18]/80 backdrop-blur text-slate-200 hover:text-white transition-colors" aria-label="Download image"><Download className="w-4 h-4" /></a>
      </div>
    </div>
  );
};

export const MessageBubble = ({ message, catalog }) => {
  const isUser = message.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end rise" data-testid={`message-user-${message.id}`}>
        <div className="bg-indigo-600/20 text-indigo-50 border border-indigo-500/30 rounded-2xl rounded-tr-sm px-5 py-3.5 max-w-[85%] text-[15px] leading-relaxed">
          {message.reference_preview && <img src={message.reference_preview} alt="Reference" className="mb-3 rounded-lg max-h-40 object-cover border border-indigo-500/30" data-testid={`reference-image-${message.id}`} />}
          {message.had_image && !message.reference_preview && <p className="mb-2 text-xs text-indigo-300/80 flex items-center gap-1.5"><Paperclip className="w-3 h-3" /> Reference photo was attached</p>}
          <span className="whitespace-pre-wrap">{message.content}</span>
        </div>
      </div>
    );
  }
  const meta = PROVIDER_META[message.provider];
  const model = findModel(catalog, message.provider, message.model);
  const copyAll = () => { navigator.clipboard.writeText(message.content); toast.success("Response copied"); };
  return (
    <div className="flex justify-start rise" data-testid={`message-assistant-${message.id}`}>
      <div className="bg-[#141726] text-slate-200 border border-slate-800 rounded-2xl rounded-tl-sm px-6 py-4 max-w-[90%] shadow-lg min-w-0">
        {message.images?.length > 0 && (
          <div className="grid gap-3 mb-3" data-testid={`message-images-${message.id}`}>
            {message.images.map((src, i) => <GeneratedImage key={i} src={src} message={message} index={i} />)}
          </div>
        )}
        {message.had_image && !message.images && (
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2" data-testid={`image-expired-${message.id}`}><ImageOff className="w-3.5 h-3.5" /> Image was shown once — pinned images live in your gallery</div>
        )}
        {message.content && <Markdown>{message.content}</Markdown>}
        <div className="mt-3 pt-3 border-t border-slate-800/80 flex items-center gap-3 text-[11px] text-slate-500">
          <span className={`px-2 py-0.5 rounded-full border ${meta?.badge || "border-slate-700"}`}>{model?.name || message.model}</span>
          {message.elapsed != null && <span>{message.elapsed}s</span>}
          <button onClick={copyAll} className="ml-auto flex items-center gap-1 hover:text-slate-200 transition-colors" data-testid={`copy-response-${message.id}`}><Copy className="w-3 h-3" /> Copy</button>
        </div>
      </div>
    </div>
  );
};

export const TypingBubble = ({ text, catalog, provider, model, isImage, thinking }) => {
  const m = findModel(catalog, provider, model);
  return (
    <div className="flex justify-start" data-testid="streaming-message">
      <div className="bg-[#141726] text-slate-200 border border-slate-800 rounded-2xl rounded-tl-sm px-6 py-4 max-w-[90%] shadow-lg min-w-0">
        {text ? <Markdown>{text}</Markdown> : (
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></div>
            <span className="text-xs text-slate-500">{m?.name || model} is {isImage ? "rendering your image (can take up to a minute)" : thinking ? "thinking deeply — this may take a while" : "thinking"}</span>
          </div>
        )}
      </div>
    </div>
  );
};
